import React, {useState, useRef, useEffect} from 'react'
import PropTypes from 'prop-types'
import {
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    Box,
    Heading,
    Tabs,
    TabList,
    TabPanels,
    TabPanel,
    Tab,
    Text,
    useBreakpointValue
} from '@chakra-ui/react'
import {Component} from '@salesforce/commerce-sdk-react/components'
import {useLocation} from 'react-router-dom'
import {ChevronDownIcon, ChevronRightIcon, ChevronUpIcon} from '../../../components/custom-icons'
import {HideOnDesktop, HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive'
import shadows from '../../../theme/foundations/shadows'

/**
 * PDC Accordion Block : Wrapper for the legal pages main region
 * It renders accordions on mobile and tabs on tablet/desktop
 * Its children are PDCAccordion and possible grand-children PDCInnerAccordion
 *
 * @param {object} regions - Regions
 * @returns {JSX.Element} - PDC Anchors Block component
 */
export const PDCAccordionBlock = ({regions}) => {
    const {hash} = useLocation()
    const accordionReduceMotion = Boolean(hash)

    let components = []
    if (regions) {
        components = regions[0].components || []
    }

    const isMobile = useBreakpointValue({base: true, md: false})

    // Get index of the component where anchorId is equal to hash
    const anchorComponentIndex = components.findIndex((component) => {
        return component?.data?.anchorId === hash?.replace('#', '')
    })

    // Mobile accordion: no default open if no anchor
    const [activeAccordionIndex, setActiveAccordionIndex] = useState(
        anchorComponentIndex !== -1 ? anchorComponentIndex : undefined
    )

    // Desktop tabs: default to 0 if no anchor
    const [activeTabIndex, setActiveTabIndex] = useState(
        anchorComponentIndex !== -1 ? anchorComponentIndex : 0
    )

    useEffect(() => {
        if (anchorComponentIndex === -1) return

        if (isMobile) {
            // Mobile (Accordion)
            setActiveAccordionIndex(anchorComponentIndex)
        } else {
            // Desktop (Tabs)
            setActiveTabIndex(anchorComponentIndex)
        }
    }, [hash, anchorComponentIndex, isMobile])

    const tabPanelCSS = {
        padding: '24px',
        _focus: {
            boxShadow: shadows.outline
        }
    }

    return (
        <Box
            p={{base: '20px 10px 24px', lg: '20px 40px 32px'}}
            backgroundColor={'whiteSmoke'}
            className="pdc-accordion-block-wrapper"
        >
            <HideOnDesktop>
                <Box backgroundColor={'white'}>
                    <Accordion
                        allowToggle
                        my="-1"
                        index={activeAccordionIndex}
                        onChange={setActiveAccordionIndex}
                        as="ul"
                        listStyleType="none"
                        // reduceMotion:
                        // Disable Accordion animations to avoid hash‑scroll timing issues.
                        // Hash scrolling fails if the scroll runs before the old tab collapses
                        // and the target tab finishes expanding. No animation = stable layout.
                        reduceMotion={accordionReduceMotion}
                    >
                        {components.map((component, idx) => {
                            return (
                                <AccordionItem
                                    key={component?.id || idx}
                                    mb="-1"
                                    borderWidth={0}
                                    sx={{'&:last-of-type': {borderBottomWidth: '0'}}}
                                    as="li"
                                >
                                    {({isExpanded}) => (
                                        <Box
                                            // do not duplicate ID in DOM
                                            id={isMobile ? component.data?.anchorId : undefined}
                                            // block scroll to target until accordion tab is opened
                                            data-hash-scroll-lock={isExpanded ? 'false' : 'true'}
                                        >
                                            <Heading as="h2">
                                                <AccordionButton
                                                    borderBottomWidth="0"
                                                    backgroundColor={isExpanded ? 'blue' : 'white'}
                                                    _hover={
                                                        isExpanded
                                                            ? {backgroundColor: 'blue'}
                                                            : {backgroundColor: 'white'}
                                                    }
                                                    p="20px 24px"
                                                    display={'flex'}
                                                    alignItems={'center'}
                                                    justifyContent={'space-between'}
                                                    color={isExpanded ? 'white' : 'fullBlack'}
                                                >
                                                    <Text
                                                        as="span"
                                                        align="left"
                                                        variant="eyeBrowLarge"
                                                        mr="10px"
                                                    >
                                                        {component?.data?.title}
                                                    </Text>
                                                    {isExpanded ? (
                                                        <ChevronUpIcon boxSize="12px" />
                                                    ) : (
                                                        <ChevronDownIcon boxSize="12px" />
                                                    )}
                                                </AccordionButton>
                                            </Heading>
                                            <AccordionPanel p="24px 16px" as="section">
                                                <Component component={component} />
                                            </AccordionPanel>
                                        </Box>
                                    )}
                                </AccordionItem>
                            )
                        })}
                    </Accordion>
                </Box>
            </HideOnDesktop>

            <HideOnMobile>
                <Tabs orientation="vertical" index={activeTabIndex} onChange={setActiveTabIndex}>
                    <TabList
                        backgroundColor={'white'}
                        w="33%"
                        h="fit-content"
                        borderWidth={'0'}
                        as="ul"
                    >
                        {components.map((component, index) => {
                            return (
                                <Tab
                                    key={component?.id || index}
                                    display={'flex'}
                                    alignItems={'center'}
                                    justifyContent={'space-between'}
                                    textAlign={'left'}
                                    textStyle="eyeBrowLarge"
                                    fontSize={'14px'}
                                    p="20px 24px"
                                    _selected={{color: 'white', backgroundColor: 'blue'}}
                                    as="li"
                                >
                                    {component?.data?.title} <ChevronRightIcon boxSize="12px" />
                                </Tab>
                            )
                        })}
                    </TabList>
                    <TabPanels
                        // key change is for forcing to track hash scroll ID
                        key={activeTabIndex}
                        ml={[null, null, '20px', '57px']}
                        backgroundColor={'white'}
                        borderWidth={'1px'}
                        borderColor="#F0F0F0"
                    >
                        {components.map((component, index) => {
                            return (
                                <TabPanel key={component?.id || index} sx={tabPanelCSS}>
                                    <Box
                                        // do not duplicate ID in DOM
                                        id={!isMobile ? component.data?.anchorId : undefined}
                                        data-hash-scroll-lock={
                                            activeTabIndex === index ? 'false' : 'true'
                                        }
                                    >
                                        <Component component={component} />
                                    </Box>
                                </TabPanel>
                            )
                        })}
                    </TabPanels>
                </Tabs>
            </HideOnMobile>
        </Box>
    )
}

PDCAccordionBlock.propTypes = {
    regions: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            components: PropTypes.arrayOf(
                PropTypes.shape({
                    data: PropTypes.object,
                    id: PropTypes.string,
                    typeId: PropTypes.string
                })
            )
        })
    )
}
